import { AlertCircle } from "lucide-react";
import { useId, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import type { SelectOption } from "@/shared/lib/localeOptions";
import { FieldContext } from "./field-context";
import { Select } from "./select";
import { Switch } from "./switch";

type FormFieldProps = {
  label: ReactNode;
  /** Texto de ajuda abaixo do controle. (`help` é sinônimo.) */
  hint?: ReactNode;
  help?: ReactNode;
  /** Mensagem de erro: troca a ajuda, pinta o controle e vira aria-describedby/aria-invalid. */
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** id do controle (gerado se omitido). Controles do kit (Input, Select, Textarea) usam-no sozinhos. */
  htmlFor?: string;
  /** Texto/elemento à direita do rótulo (ex.: contador "12/80", link "Gerar"). */
  labelAside?: ReactNode;
  children: ReactNode;
  className?: string;
};

/**
 * Rótulo + controle + ajuda/erro, acessível por padrão.
 *
 * <FormField label="E-mail" error={errors.email} required>
 *   <Input type="email" value={…} onChange={…} />
 * </FormField>
 */
export function FormField({ label, hint, help, error, required, disabled, htmlFor, labelAside, children, className }: FormFieldProps) {
  const autoId = useId();
  const id = htmlFor ?? `f${autoId}`;
  const helpText = help ?? hint;
  const descId = error ? `${id}-err` : helpText ? `${id}-help` : undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy: descId, invalid: Boolean(error), required, disabled }}>
      <div className={cn("min-w-0 space-y-1.5", className)}>
        <div className="flex items-baseline justify-between gap-2">
          <label htmlFor={id} className={cn("block text-body-sm font-medium text-fg", disabled && "text-fg-disabled")}>
            {label}
            {required ? (
              <span className="ml-0.5 text-danger-fg" aria-hidden>
                *
              </span>
            ) : null}
          </label>
          {labelAside ? <span className="text-caption text-fg-subtle">{labelAside}</span> : null}
        </div>
        {children}
        {error ? (
          <p id={descId} className="flex items-start gap-1 text-caption text-danger-fg" role="alert">
            <AlertCircle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        ) : helpText ? (
          <p id={descId} className="text-caption text-fg-subtle">
            {helpText}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

type SelectFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  error?: ReactNode;
  hint?: ReactNode;
};

/** Atalho FormField + Select. */
export function SelectField({ label, value, onChange, options, placeholder = "Selecione…", className, error, hint }: SelectFieldProps) {
  return (
    <FormField label={label} className={className} error={error} hint={hint}>
      <Select value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} options={options} />
    </FormField>
  );
}

type TogglePillProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
  subtitle?: string;
  disabled?: boolean;
};

/** Opção alternável em formato de chip (seleção múltipla compacta: perfis, tags, filas). */
export function TogglePill({ checked, onChange, children, subtitle, disabled }: TogglePillProps) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "inline-flex flex-col items-start rounded-md border px-3 py-1.5 text-left text-body-sm font-medium transition-colors duration-150",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked
          ? "border-brand-accent/50 bg-brand-subtle text-brand-accent"
          : "border-border-strong bg-surface text-fg-muted hover:bg-surface-hover hover:text-fg",
      )}
    >
      <span>{children}</span>
      {subtitle ? <span className="mt-0.5 font-mono text-[0.625rem] font-normal opacity-75">{subtitle}</span> : null}
    </button>
  );
}

type FormSectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
};

/** Grupo de campos dentro de diálogos/formulários longos. */
export function FormSection({ title, description, children, className }: FormSectionProps) {
  return (
    <fieldset className={cn("min-w-0 space-y-3 border-t border-border pt-4 first:border-t-0 first:pt-0", className)}>
      <legend className="sr-only">{title}</legend>
      <div aria-hidden>
        <h3 className="text-title-sm text-fg">{title}</h3>
        {description ? <p className="mt-0.5 text-caption text-fg-subtle">{description}</p> : null}
      </div>
      {children}
    </fieldset>
  );
}

/** Linha com rótulo + descrição + Switch (configurações liga/desliga). */
export function SwitchField({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface px-4 py-3">
      <Switch label={label} description={description} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
